import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';

const PRIMARY = '#6fb5e0';

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

const IconCastle = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 20v-9H2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2Z"/>
    <path d="M18 11V4H6v7"/>
    <path d="M15 22v-4a3 3 0 0 0-3-3a3 3 0 0 0-3 3v4"/>
    <path d="M22 11V4"/>
    <path d="M2 11V4"/>
    <path d="M6 4V2"/>
    <path d="M18 4V2"/>
    <path d="M10 4V2"/>
    <path d="M14 4V2"/>
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5"/>
  </svg>
);

const IconChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6"/>
  </svg>
);

const IconUpload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const IconPencil = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
    <path d="m15 5 4 4"/>
  </svg>
);

const IconShield = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const IconBanknote = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="12" rx="2"/>
    <circle cx="12" cy="12" r="2"/>
    <path d="M6 12h.01M18 12h.01"/>
  </svg>
);

const IconClock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

const STEP_LABELS = ['BANNER', 'HERALD', 'ALLIES', 'TREASURY', 'CHARTER'];

const ProgressBar = ({ currentStep }) => (
  <nav aria-label="Progress" className="max-w-3xl mx-auto px-4 pt-8">
    <ol className="flex items-center justify-between">
      {STEP_LABELS.map((label, idx) => {
        const stepNum = idx + 1;
        const isCompleted = stepNum < currentStep;
        const isCurrent = stepNum === currentStep;
        return (
          <li key={label} className="flex-1 flex items-center">
            <div className="flex flex-col items-center">
              <div
                className="flex items-center justify-center rounded-full border-2 font-mono"
                style={{
                  width: 28,
                  height: 28,
                  fontSize: 11,
                  borderColor: isCompleted || isCurrent ? PRIMARY : '#2c3350',
                  backgroundColor: isCurrent ? PRIMARY : 'transparent',
                  color: isCompleted ? PRIMARY : isCurrent ? '#fff' : '#6a789f',
                }}
                aria-current={isCurrent ? 'step' : undefined}
              >
                {isCompleted ? <IconCheck /> : stepNum}
              </div>
              <span
                className="mt-1 hidden sm:inline font-mono tracking-widest"
                style={{ fontSize: 11, color: isCompleted || isCurrent ? PRIMARY : '#6a789f' }}
              >
                {label}
              </span>
            </div>
            {idx < STEP_LABELS.length - 1 && (
              <div
                className="flex-1 h-px mx-2"
                style={{ backgroundColor: isCompleted ? PRIMARY : '#1c1f2d' }}
              />
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);

// ---------------------------------------------------------------------------
// Step 1 — Banner
// ---------------------------------------------------------------------------

const StepBanner = ({ wizardData, onNext }) => {
  const [firstName, setFirstName] = useState(wizardData.first_name || '');
  const [lastName, setLastName] = useState(wizardData.last_name || '');
  const [email, setEmail] = useState(wizardData.email || '');
  const [emailStatus, setEmailStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [password, setPassword] = useState(wizardData.password || '');
  const [confirmPassword, setConfirmPassword] = useState(wizardData.confirmPassword || '');
  const [handle, setHandle] = useState(wizardData.handle || '');
  const [status, setStatus] = useState(null); // null | 'checking' | 'available' | 'unavailable' | 'invalid'
  const [handleError, setHandleError] = useState('');
  const [formError, setFormError] = useState('');
  const debounceRef = useRef(null);
  const emailDebounceRef = useRef(null);

  const checkEmail = useCallback(async (val) => {
    if (!val || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)) { setEmailStatus(null); return; }
    setEmailStatus('checking');
    try {
      const res = await fetch(`${config.API_URL}/onboarding/email-check?email=${encodeURIComponent(val)}`);
      const data = await res.json();
      setEmailStatus(data.available ? 'available' : 'taken');
    } catch {
      setEmailStatus(null);
    }
  }, []);

  const checkHandle = useCallback(async (val) => {
    if (!val) { setStatus(null); return; }
    setStatus('checking');
    try {
      const res = await fetch(`${config.API_URL}/onboarding/handle-check?handle=${encodeURIComponent(val)}`);
      const data = await res.json();
      if (data.error) { setStatus('invalid'); setHandleError(data.error); }
      else if (data.available) { setStatus('available'); setHandleError(''); }
      else { setStatus('unavailable'); setHandleError(`${val} is already taken.`); }
    } catch {
      setStatus(null);
    }
  }, []);

  // Auto-check on mount if fields are pre-filled (e.g. user navigated back)
  useEffect(() => {
    if (handle) checkHandle(handle);
    if (email) checkEmail(email);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    setEmailStatus(null);
    clearTimeout(emailDebounceRef.current);
    emailDebounceRef.current = setTimeout(() => checkEmail(val), 500);
  };

  const handleHandleChange = (e) => {
    const val = e.target.value;
    setHandle(val);
    setStatus('checking');
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => checkHandle(val), 400);
  };

  const canSubmit = status === 'available' && emailStatus !== 'taken' && emailStatus !== 'checking';

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!firstName.trim() || !lastName.trim()) { setFormError('First and last name are required.'); return; }
    if (!email.trim()) { setFormError('Email is required.'); return; }
    if (emailStatus === 'taken') { setFormError('That email is already in use.'); return; }
    if (password.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setFormError('Passwords do not match.'); return; }
    if (status !== 'available') return;
    onNext({ first_name: firstName.trim(), last_name: lastName.trim(), email: email.trim(), password, handle });
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-2xl font-bold mb-1">Plant your banner</h2>
      <p className="text-gray-400 mb-8">Create your account and claim your Castle address.</p>

      {formError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-900 border border-red-700 text-red-300 text-sm">
          {formError}
        </div>
      )}

      <div className="space-y-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
          <p className="text-sm font-semibold text-gray-300">Your account</p>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            />
            <input
              type="text"
              placeholder="Last name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={handleEmailChange}
              required
              className={`w-full px-4 py-3 rounded-lg bg-gray-800 border text-white placeholder-gray-500 focus:outline-none transition-colors ${emailStatus === 'taken' ? 'border-red-500 focus:border-red-500' : emailStatus === 'available' ? 'border-green-600 focus:border-green-500' : 'border-gray-700 focus:border-blue-500'}`}
            />
            {emailStatus === 'checking' && <p className="text-gray-500 text-xs mt-1">Checking…</p>}
            {emailStatus === 'taken' && <p className="text-red-400 text-xs mt-1">An account with that email already exists.</p>}
          </div>
          <input
            type="password"
            placeholder="Password (min 8 characters)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
          />
          <input
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <label className="block text-sm font-semibold text-gray-300 mb-3">Your handle</label>
          <div className="flex rounded-lg overflow-hidden border border-gray-700 focus-within:border-blue-500 transition-colors">
            <span className="flex items-center px-4 bg-gray-800 text-gray-500 font-mono text-sm whitespace-nowrap select-none">
              mycastle.gg/
            </span>
            <input
              type="text"
              value={handle}
              onChange={handleHandleChange}
              placeholder="yourname"
              className="flex-1 bg-transparent px-4 py-3 text-white font-mono focus:outline-none"
            />
          </div>
          <div className="mt-3 min-h-[20px]">
            {status === 'checking' && (
              <span className="text-gray-500 text-sm">Checking availability…</span>
            )}
            {status === 'available' && (
              <span className="text-green-400 text-sm flex items-center gap-1">
                <IconCheck /> <code className="font-mono">mycastle.gg/{handle}</code> is yours.
              </span>
            )}
            {(status === 'unavailable' || status === 'invalid') && (
              <span className="text-red-400 text-sm">{handleError}</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <button
          type="submit"
          disabled={!canSubmit}
          className="px-6 py-2.5 rounded-lg font-semibold text-white transition-opacity disabled:opacity-40"
          style={{ backgroundColor: PRIMARY }}
        >
          Raise your banner
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Step 2 — Herald
// ---------------------------------------------------------------------------

const StepHerald = ({ wizardData, onNext, onBack }) => {
  const [displayName, setDisplayName] = useState(wizardData.display_name || '');
  const [thankYouMessage, setThankYouMessage] = useState(wizardData.thank_you_message || '');
  const [accentColor, setAccentColor] = useState(wizardData.accent_color || '#C9A14A');
  const [hexInput, setHexInput] = useState(wizardData.accent_color || '#C9A14A');
  const [avatarFile, setAvatarFile] = useState(wizardData.avatarFile || null);
  const [avatarPreview, setAvatarPreview] = useState(wizardData.logo_url || null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5 MB.'); return; }
    setError('');
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleColorPicker = (e) => {
    setAccentColor(e.target.value);
    setHexInput(e.target.value);
  };

  const handleHexInput = (e) => {
    const val = e.target.value;
    setHexInput(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) setAccentColor(val);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!displayName.trim()) { setError('Display name is required.'); return; }
    onNext({ display_name: displayName.trim(), thank_you_message: thankYouMessage, accent_color: accentColor, avatarFile, logo_url: avatarPreview });
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-2xl font-bold mb-1">Arm your herald</h2>
      <p className="text-gray-400 mb-8">Your name and sigil are shown to everyone who visits your page.</p>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-6">
        {/* Avatar */}
        <div className="flex items-center gap-5">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="relative rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center border-2 text-gray-500 hover:opacity-80 transition-opacity"
            style={{ width: 80, height: 80, borderColor: accentColor }}
          >
            {avatarPreview
              ? <img src={avatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
              : <span className="text-2xl font-bold">?</span>}
          </button>
          <div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-700 text-sm text-gray-300 hover:bg-gray-800 transition-colors"
            >
              <IconUpload /> Upload image
            </button>
            <p className="text-xs text-gray-500 mt-1">PNG, JPG, or GIF · max 5 MB</p>
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif" className="hidden" onChange={handleFileChange} />
        </div>

        {/* Display name */}
        <div>
          <label className="block text-sm font-semibold text-gray-300 mb-1">Display name</label>
          <input
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            maxLength={50}
            placeholder="Sir Streamsalot"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
            required
          />
        </div>

        {/* Thank You Message */}
        <div>
          <label className="block text-sm font-semibold text-gray-300 mb-1">Thank You Message</label>
          <textarea
            value={thankYouMessage}
            onChange={e => setThankYouMessage(e.target.value)}
            maxLength={1000}
            rows={3}
            placeholder="Thanks so much for the support! It means the world to me."
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors resize-none"
          />
          <p className="text-xs text-gray-500 mt-1 text-right">{thankYouMessage.length}/1000</p>
        </div>

        {/* Accent color */}
        <div>
          <label className="block text-sm font-semibold text-gray-300 mb-2">Accent color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={accentColor}
              onChange={handleColorPicker}
              className="w-10 h-10 rounded cursor-pointer border-0 bg-transparent p-0"
            />
            <input
              type="text"
              value={hexInput}
              onChange={handleHexInput}
              maxLength={7}
              placeholder="#C9A14A"
              className="w-28 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white font-mono text-sm focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>

      <div className="flex justify-between mt-6">
        <button type="button" onClick={onBack} className="flex items-center gap-1 px-4 py-2.5 text-gray-400 hover:text-white transition-colors">
          <IconChevronLeft /> Back
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 rounded-lg font-semibold text-white"
          style={{ backgroundColor: PRIMARY }}
        >
          March onward
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Step 3 — Allies
// ---------------------------------------------------------------------------

const PLATFORMS = [
  { key: 'twitch',   prefix: 'twitch.tv/',     placeholder: 'twitch' },
  { key: 'youtube',  prefix: 'youtube.com/@',   placeholder: 'youtube' },
  { key: 'discord',  prefix: 'discord.gg/',     placeholder: 'discord' },
  { key: 'tiktok',   prefix: 'tiktok.com/@',    placeholder: 'tiktok' },
  { key: 'twitter',  prefix: 'x.com/',          placeholder: 'x' },
  { key: 'kick',     prefix: 'kick.com/',       placeholder: 'kick' },
];

const PLATFORM_ICONS = {
  twitch:  <span className="font-bold text-purple-400 text-xs">TV</span>,
  youtube: <span className="font-bold text-red-400 text-xs">YT</span>,
  discord: <span className="font-bold text-indigo-400 text-xs">DC</span>,
  tiktok:  <span className="font-bold text-pink-400 text-xs">TK</span>,
  twitter: <span className="font-bold text-sky-400 text-xs">X</span>,
  kick:    <span className="font-bold text-green-400 text-xs">KK</span>,
};

const StepAllies = ({ wizardData, onNext, onBack }) => {
  const initHandles = () => {
    const map = {};
    (wizardData.platforms || []).forEach(p => { map[p.platform] = p.handle; });
    return map;
  };
  const [handles, setHandles] = useState(initHandles);

  const handleContinue = (e) => {
    e.preventDefault();
    const platforms = PLATFORMS
      .filter(p => handles[p.key]?.trim())
      .map(p => ({ platform: p.key, handle: handles[p.key].trim() }));
    onNext({ platforms });
  };

  const handleSkip = () => onNext({ platforms: [] });

  return (
    <form onSubmit={handleContinue}>
      <h2 className="text-2xl font-bold mb-1">Call upon your allies</h2>
      <p className="text-gray-400 mb-8">Connect platforms where your audience already lives. You can skip and add these later.</p>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
        {PLATFORMS.map(p => (
          <div key={p.key} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded flex items-center justify-center bg-gray-800 flex-shrink-0">
              {PLATFORM_ICONS[p.key]}
            </div>
            <div className="flex-1 flex rounded-lg overflow-hidden border border-gray-700 focus-within:border-blue-500 transition-colors">
              <span className="flex items-center px-3 bg-gray-800 text-gray-500 font-mono text-xs whitespace-nowrap select-none">
                {p.prefix}
              </span>
              <input
                type="text"
                value={handles[p.key] || ''}
                onChange={e => setHandles(prev => ({ ...prev, [p.key]: e.target.value }))}
                placeholder={p.placeholder}
                className="flex-1 bg-transparent px-3 py-2.5 text-white text-sm focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-6">
        <button type="button" onClick={onBack} className="flex items-center gap-1 px-4 py-2.5 text-gray-400 hover:text-white transition-colors">
          <IconChevronLeft /> Back
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSkip}
            className="px-5 py-2.5 rounded-lg font-semibold text-gray-400 border border-gray-700 hover:bg-gray-800 transition-colors"
          >
            Skip for now
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 rounded-lg font-semibold text-white"
            style={{ backgroundColor: PRIMARY }}
          >
            Continue
          </button>
        </div>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Step 4 — Treasury
// ---------------------------------------------------------------------------

const StepTreasury = ({ onNext, onBack }) => {
  const handleLater = () => {
    onNext({});
  };

  const features = [
    { icon: <IconShield />, title: 'Bank-grade security', desc: "Your payouts are protected by Stripe's industry-leading fraud tools." },
    { icon: <IconBanknote />, title: 'Chargeback Support', desc: 'Support in fighting chargebacks, with support from the Castle team.' },
    { icon: <IconClock />, title: '2-minute setup', desc: 'Connect your bank once — no paperwork, no waiting.' },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">Fortify the Treasury</h2>
      <p className="text-gray-400 mb-8">Castle partners with Stripe to handle payouts. We'll set up your account here — no forms to fill out twice.</p>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          {features.map(f => (
            <div key={f.title} className="flex flex-col items-start gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${PRIMARY}1a` }}>
                <span style={{ color: PRIMARY }}>{f.icon}</span>
              </div>
              <div>
                <p className="font-bold text-sm">{f.title}</p>
                <p className="text-gray-400 text-sm mt-1">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="border border-dashed border-gray-700 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-xs tracking-widest text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded">COMING SOON</span>
          </div>
          <p className="text-gray-400 text-sm">
            Stripe Connect onboarding launches with the next release. You can finish claiming your castle now
            and connect payouts before your first tip.
          </p>
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <button type="button" onClick={onBack} className="flex items-center gap-1 px-4 py-2.5 text-gray-400 hover:text-white transition-colors">
          <IconChevronLeft /> Back
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            disabled
            className="px-5 py-2.5 rounded-lg font-semibold border border-gray-700 text-gray-500 cursor-not-allowed opacity-40"
          >
            Open Stripe setup
          </button>
          <button
            type="button"
            onClick={handleLater}
            className="px-6 py-2.5 rounded-lg font-semibold text-white"
            style={{ backgroundColor: PRIMARY }}
          >
            I'll do this later
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 5 — Charter (Review)
// ---------------------------------------------------------------------------

const StepCharter = ({ wizardData, email, onGoToStep, onSubmit, submitting, error }) => {
  const [agreed, setAgreed] = useState(false);

  const rows = [
    {
      label: 'EMAIL',
      value: <span className="text-gray-300">{email}</span>,
      edit: () => onGoToStep(1),
    },
    {
      label: 'HANDLE',
      value: <span className="font-mono text-gray-300">mycastle.gg/{wizardData.handle || '—'}</span>,
      edit: () => onGoToStep(1),
    },
    {
      label: 'HERALD',
      value: (
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full overflow-hidden border-2 flex items-center justify-center bg-gray-800 flex-shrink-0"
            style={{ borderColor: wizardData.accent_color || '#C9A14A' }}
          >
            {wizardData.logo_url
              ? <img src={wizardData.logo_url} alt="avatar" className="w-full h-full object-cover" />
              : <span className="text-gray-500 text-sm">?</span>}
          </div>
          <span className="text-gray-300">{wizardData.display_name || '—'}</span>
        </div>
      ),
      edit: () => onGoToStep(2),
    },
    {
      label: 'ALLIES',
      value: wizardData.platforms && wizardData.platforms.length > 0
        ? <span className="text-gray-300">{wizardData.platforms.map(p => p.platform).join(', ')}</span>
        : <span className="text-gray-500">None yet</span>,
      edit: () => onGoToStep(3),
    },
    {
      label: 'TREASURY',
      value: <span className="text-gray-500">Not connected — finish later</span>,
      edit: () => onGoToStep(4),
    },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">Review your charter</h2>
      <p className="text-gray-400 mb-8">One last look before you raise the drawbridge.</p>

      <div className="space-y-3">
        {rows.map(row => (
          <div key={row.label} className="bg-gray-900 border border-gray-800 rounded-xl px-5 py-4 flex items-center gap-4">
            <span className="font-mono text-xs tracking-widest text-gray-500 w-20 flex-shrink-0">{row.label}</span>
            <div className="flex-1">{row.value}</div>
            {row.edit && (
              <button
                type="button"
                onClick={row.edit}
                className="text-gray-500 hover:text-white transition-colors p-1"
                aria-label={`Edit ${row.label}`}
              >
                <IconPencil />
              </button>
            )}
          </div>
        ))}
      </div>

      <label className="flex items-start gap-3 mt-6 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
          className="mt-1 accent-blue-500"
        />
        <span className="text-gray-400 text-sm">
          I confirm the information above is correct and agree to the Castle Creator Terms.
        </span>
      </label>

      {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

      <div className="flex justify-between mt-6">
        <button type="button" onClick={() => onGoToStep(4)} className="flex items-center gap-1 px-4 py-2.5 text-gray-400 hover:text-white transition-colors">
          <IconChevronLeft /> Back
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!agreed || submitting}
          className="px-6 py-2.5 rounded-lg font-semibold text-white transition-opacity disabled:opacity-40"
          style={{ backgroundColor: PRIMARY }}
        >
          {submitting ? 'Raising the drawbridge…' : 'Raise the drawbridge'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main wizard
// ---------------------------------------------------------------------------

export default function OnboardingWizard() {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({ accent_color: '#C9A14A', platforms: [] });
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState('');

  const mergeData = (updates) => setWizardData(prev => ({ ...prev, ...updates }));

  const goNext = (updates = {}) => {
    mergeData(updates);
    setCurrentStep(s => s + 1);
  };

  const goBack = () => setCurrentStep(s => s - 1);
  const goToStep = (n) => setCurrentStep(n);

  const handleComplete = async () => {
    setCompleting(true);
    setCompleteError('');
    try {
      const form = new FormData();
      form.append('first_name', wizardData.first_name || '');
      form.append('last_name', wizardData.last_name || '');
      form.append('email', wizardData.email || '');
      form.append('password', wizardData.password || '');
      form.append('handle', wizardData.handle || '');
      form.append('display_name', wizardData.display_name || '');
      if (wizardData.thank_you_message) form.append('thank_you_message', wizardData.thank_you_message);
      if (wizardData.accent_color) form.append('accent_color', wizardData.accent_color);
      if (wizardData.avatarFile) form.append('avatar', wizardData.avatarFile);
      form.append('platforms', JSON.stringify(wizardData.platforms || []));

      const res = await fetch(`${config.API_URL}/onboarding/register-and-save`, {
        method: 'POST',
        body: form,
      });

      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Something went wrong.');
      }

      const data = await res.json();
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('token_type', data.token_type || 'bearer');
      localStorage.setItem('user_email', data.user_email);
      localStorage.setItem('user_name', data.user_name);
      localStorage.setItem('user_id', data.user_id);

      navigate('/pending-approval');
    } catch (err) {
      setCompleteError(err.message);
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center">
          <div className="flex items-center gap-2" style={{ color: PRIMARY }}>
            <IconCastle />
            <span className="font-bold text-lg tracking-tight text-white">Castle</span>
          </div>
        </div>
      </header>

      <ProgressBar currentStep={currentStep} />

      <main className="max-w-2xl mx-auto px-4 py-12">
        {currentStep === 1 && (
          <StepBanner wizardData={wizardData} onNext={goNext} />
        )}
        {currentStep === 2 && (
          <StepHerald wizardData={wizardData} onNext={goNext} onBack={goBack} />
        )}
        {currentStep === 3 && (
          <StepAllies wizardData={wizardData} onNext={goNext} onBack={goBack} />
        )}
        {currentStep === 4 && (
          <StepTreasury onNext={goNext} onBack={goBack} />
        )}
        {currentStep === 5 && (
          <StepCharter
            wizardData={wizardData}
            email={wizardData.email || ''}
            onGoToStep={goToStep}
            onSubmit={handleComplete}
            submitting={completing}
            error={completeError}
          />
        )}
      </main>
    </div>
  );
}
